import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "../components/SiteLayout";
import SEO from "../components/SEO";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import FileUploader from "../components/FileUploader";
import DatePicker from "../components/DatePicker";
import { ShieldCheck, Loader2, CheckCircle2, ArrowRight } from "lucide-react";

const PROVIDERS = [
 "Aetna", "Blue Cross Blue Shield", "Cigna", "United Healthcare",
 "Medicare", "Medicaid", "Kaiser Permanente", "Humana", "Tricare", "Self-pay", "Other",
];

export default function Insurance() {
 const [form, setForm] = useState({
 full_name: "", email: "", phone: "", dob: "",
 provider: "", member_id: "", group_number: "", notes: "",
 });
 const [idDocs, setIdDocs] = useState([]);
 const [insuranceDocs, setInsuranceDocs] = useState([]);
 const [busy, setBusy] = useState(false);
 const [done, setDone] = useState(false);

 async function submit(e) {
 e.preventDefault();
 if (!form.full_name || !form.email || !form.phone || !form.provider || !form.member_id) {
 return toast.error("Please complete name, email, phone, provider, and member ID.");
 }
 setBusy(true);
 try {
 const documents = [
 ...idDocs.map((d) => ({ id: d.id, original_name: d.original_name, kind: "id", content_type: d.content_type })),
 ...insuranceDocs.map((d) => ({ id: d.id, original_name: d.original_name, kind: "insurance", content_type: d.content_type })),
 ];
 await api.post("/insurance-verification", { ...form, documents });
 setDone(true);
 toast.success("Verification submitted. We'll be in touch within one business day.");
 } catch (err) {
 toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
 } finally {
 setBusy(false);
 }
 }

 return (
 <SiteLayout>
 <SEO
 title="Insurance & Coverage Verification"
 description="Which insurance plans we accept, what to expect for copays, and how to verify your telepsychiatry benefits in under a minute. Self-pay rates also available."
 path="/insurance"
 />
 <Toaster richColors position="top-right" />
 <section data-testid="ins-hero" className="container-pad max-w-7xl mx-auto pt-14 lg:pt-20 pb-12">
 <div className="grid lg:grid-cols-12 gap-12 items-center">
 <div className="lg:col-span-7">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Insurance verification</p>
 <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink text-balance">
 Find out if we're in your network, <span className="italic text-sage-600">in under a minute.</span>
 </h1>
 <p className="mt-6 text-lg text-muted_text leading-relaxed max-w-2xl">
 Share your plan details and (optionally) upload your insurance card. We'll confirm coverage and respond within one business day, no commitment required.
 </p>
 </div>
 <div className="lg:col-span-5">
 <div className="relative">
 <img
 src="/insurance-hero.png"
 alt="Insurance, quality care, greater access. We work with leading insurance plans to make compassionate psychiatric care more accessible and affordable."
 className="rounded-3xl w-full h-auto object-contain shadow-[0_30px_60px_-30px_rgba(26,43,51,0.3)] bg-cream"
 loading="lazy"
 />
 <div className="absolute -top-6 -left-6 w-32 h-32 rounded-3xl bg-gold-500/15 -z-10" />
 <div className="absolute -bottom-8 -right-6 w-40 h-40 rounded-3xl bg-sage-500/15 -z-10" />
 </div>
 </div>
 </div>
 </section>

 <section className="container-pad max-w-7xl mx-auto pb-24">
 <div className="grid lg:grid-cols-12 gap-10">
 <div className="lg:col-span-8">
 {done ? (
 <div data-testid="ins-success" className="card-soft p-10 text-center">
 <div className="w-14 h-14 mx-auto rounded-2xl bg-sage-500/15 text-sage-700 flex items-center justify-center">
 <CheckCircle2 className="w-7 h-7" strokeWidth={1.75} />
 </div>
 <h3 className="mt-5 font-heading text-2xl font-semibold text-ink">Verification submitted</h3>
 <p className="mt-3 text-muted_text leading-relaxed max-w-md mx-auto">
 Thanks, {form.full_name.split(" ")[0]}. We'll review your benefits and reply within one business day. Want to lock in a time meanwhile?
 </p>
 <Link to="/contact" data-testid="ins-go-book" className="btn-gold mt-8">
 Book a tentative slot <ArrowRight className="w-4 h-4" />
 </Link>
 </div>
 ) : (
 <form onSubmit={submit} data-testid="ins-form" className="card-soft p-6 sm:p-8 lg:p-10 space-y-7">
 <div>
 <p className="font-heading text-lg font-semibold text-ink">Your details</p>
 <div className="mt-4 grid sm:grid-cols-2 gap-5">
 <div>
 <Label htmlFor="i_name">Full name</Label>
 <Input id="i_name" data-testid="ins-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="h-12 mt-2" required />
 </div>
 <div>
 <Label htmlFor="i_email">Email</Label>
 <Input id="i_email" type="email" data-testid="ins-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 mt-2" required />
 </div>
 <div>
 <Label htmlFor="i_phone">Phone</Label>
 <Input id="i_phone" data-testid="ins-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 mt-2" required />
 </div>
 <div>
 <Label htmlFor="i_dob">Date of birth <span className="text-muted_text font-normal">(optional)</span></Label>
 <div className="mt-2">
 <DatePicker
 testId="ins-dob"
 value={form.dob}
 onChange={(v) => setForm({ ...form, dob: v })}
 placeholder="Select date of birth"
 maxDate={new Date()}
 />
 </div>
 </div>
 </div>
 </div>

 <div>
 <p className="font-heading text-lg font-semibold text-ink">Insurance plan</p>
 <div className="mt-4 grid sm:grid-cols-2 gap-5">
 <div>
 <Label htmlFor="i_provider">Provider</Label>
 <select
 id="i_provider"
 data-testid="ins-provider"
 value={form.provider}
 onChange={(e) => setForm({ ...form, provider: e.target.value })}
 className="h-12 mt-2 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
 required
 >
 <option value="" disabled>Select your provider</option>
 {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
 </select>
 </div>
 <div>
 <Label htmlFor="i_mid">Member ID</Label>
 <Input id="i_mid" data-testid="ins-member" value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })} className="h-12 mt-2" required />
 </div>
 <div>
 <Label htmlFor="i_grp">Group number <span className="text-muted_text font-normal">(optional)</span></Label>
 <Input id="i_grp" data-testid="ins-group" value={form.group_number} onChange={(e) => setForm({ ...form, group_number: e.target.value })} className="h-12 mt-2" />
 </div>
 </div>
 </div>

 <div>
 <p className="font-heading text-lg font-semibold text-ink">Upload documents <span className="text-sm font-normal text-muted_text">(optional, but speeds things up)</span></p>
 <div className="mt-4 grid sm:grid-cols-2 gap-5">
 <FileUploader
 testId="ins-id-upload"
 kind="id"
 label="Government-issued ID"
 hint="Front of driver's licence, passport, or state ID."
 multiple={false}
 value={idDocs}
 onChange={setIdDocs}
 />
 <FileUploader
 testId="ins-card-upload"
 kind="insurance"
 label="Insurance / Medicaid card"
 hint="Front and back if possible."
 multiple
 value={insuranceDocs}
 onChange={setInsuranceDocs}
 />
 </div>
 </div>

 <div>
 <Label htmlFor="i_notes">Anything we should know? <span className="text-muted_text font-normal">(optional)</span></Label>
 <Textarea id="i_notes" data-testid="ins-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 min-h-[110px]" placeholder="Secondary insurance, mental-health rider questions, preferred clinician gender, etc." />
 </div>

 <div className="pt-4 border-t border-[#E8EBE4] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
 <p className="text-xs text-muted_text max-w-md">All uploads are encrypted and visible only to our care team. We never share your information.</p>
 <Button type="submit" disabled={busy} data-testid="ins-submit" className="btn-primary !rounded-full !h-12 !px-8 !text-base disabled:opacity-60">
 {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>) : "Verify my coverage"}
 </Button>
 </div>
 </form>
 )}
 </div>

 <aside className="lg:col-span-4 space-y-5">
 <div className="card-soft p-7">
 <div className="flex items-start gap-3">
 <div className="w-11 h-11 rounded-2xl bg-sage-500/15 text-sage-700 flex items-center justify-center"><ShieldCheck className="w-5 h-5" strokeWidth={1.75} /></div>
 <div>
 <p className="font-heading font-semibold text-ink">Private & encrypted</p>
 <p className="text-sm text-muted_text mt-1">Your documents are stored on a HIPAA-aligned server and accessible only to our care team.</p>
 </div>
 </div>
 </div>
 <div className="card-soft p-7">
 <p className="font-heading font-semibold text-ink">Plans we commonly accept</p>
 <ul className="mt-4 grid grid-cols-2 gap-y-2 text-sm text-ink">
 {["Aetna","Blue Cross Blue Shield","Cigna","United Healthcare","Medicare","Medicaid","Tricare","Self-pay"].map((p) => (
 <li key={p}>· {p}</li>
 ))}
 </ul>
 <p className="text-xs text-muted_text mt-4">Don't see your plan? Submit the form anyway, many networks aren't displayed publicly.</p>
 </div>
 </aside>
 </div>
 </section>
 </SiteLayout>
 );
}
